import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type HeatmapChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;
  xAxisField: string;
  yAxisField: string;

  xAxisLabel?: string;
  yAxisLabel?: string;
  rotation?: number;
  numberFormat?: string;
  labelValues?: boolean;
  alert?: {
    type: 'error' | 'warning';
    message: string;
  };
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  hideHeatLegend?: boolean;
  customMinColor?: string;
  customMaxColor?: string;
  setExternalEmbed?: (_data: types.analysis.EmbedChartData) => void;
};

const HeatmapChart = (args: HeatmapChartProps) => {
  const uiStyles = useUiStyles();

  const {
    id,
    data,
    fullScreen,
    readOnly,
    alert,
    xAxisLabel,
    yAxisLabel,
    exportTitle,
    exportSubTitle,
    download = true,
    rotation = undefined,
    xAxisField,
    yAxisField,
    setExternalEmbed,
    numberFormat,
    labelValues,
    hideHeatLegend = false,
    customMinColor,
    customMaxColor,
  } = args;

  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'heatmap-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      // Filter out items with 0 weight
      setLocalData(data);
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
        // background: theme.palette.secondary.dark,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box
          sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%', borderRadius: '8px', overflow: 'hidden' }}
        >
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={localData}
              fullScreen={fullScreen}
              download={download}
              xAxisField={xAxisField}
              yAxisField={yAxisField}
              numberFormat={numberFormat}
              labelValues={labelValues}
              rotation={rotation}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              hideHeatLegend={hideHeatLegend}
              customMinColor={customMinColor}
              customMaxColor={customMaxColor}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default HeatmapChart;
